import { FC, useRef } from 'react';
import { Flex } from 'antd';
import {
  SDropdown,
  ErrorMessage,
  SLabelTitle,
  SMailSelector,
  ModalDialog,
  StatusDropdown,
  UploadButton,
  SInput,
  SDatePicker,
} from '../..';
import { IProps } from './type';
import styles from '../../../assets/styles/index.module.scss';
import { useAppSelector } from '../../../store';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { DATE_FORMAT } from '../../../assets/conts';
import { DependentTypeEnum, modalTypes } from '../../../assets/enums';
import globalStyles from '../../../assets/styles/index.module.scss';
import { useSetOrganizationOnOpen } from '../../../hooks';

dayjs.extend(customParseFormat);

const GroupHealthInsuranceDependentsModal: FC<IProps> = (props) => {
  const { getFieldProps, setFieldValue, values, errors, touched } =
    props.formik;
  const uploadInput = useRef<HTMLInputElement | null>(null);
  const { organization } = useAppSelector(
    (state) => state.userReducer.userInfo,
  );

  useSetOrganizationOnOpen(props.open, values, setFieldValue);

  return (
    <ModalDialog
      title={props.title}
      subTitle={props.subTitle}
      open={props.open}
      handleOk={props.handleSubmit}
      handleCancel={props.handleCancel}
    >
      <Flex gap="middle" vertical>
        <Flex gap="small" vertical>
          <SLabelTitle>Şirket Bilgileri</SLabelTitle>
          <Flex vertical gap="small" className={styles.widthOneThird}>
            <SDropdown
              id="organization"
              value={
                props.organizations.find(
                  (item) =>
                    item.id ===
                    (values.organization ||
                      (organization !== 1 ? organization : undefined)),
                )?.name
              }
              placeholder="Şirket Seçiniz"
              className={globalStyles.width200px}
              disabled={organization !== 1}
              options={props.organizations.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
              onChange={(value) => setFieldValue('organization', value)}
            />
            {touched.organization && errors.organization && (
              <ErrorMessage>{errors.organization}</ErrorMessage>
            )}
          </Flex>
        </Flex>
        <Flex gap="small" vertical>
          <UploadButton
            fileName={[...values.upload_files]?.map((item) => item.name)}
            uploadRef={uploadInput}
            onChange={props.handleUploadFile}
          />
          <Flex gap="small" vertical>
            <SLabelTitle>Kişi Bilgileri</SLabelTitle>
            <Flex justify="space-between">
              <Flex vertical gap="small" className={styles.widthOneThird}>
                <SInput placeholder="İsim" {...getFieldProps('first_name')} />
                {touched.first_name && errors.first_name && (
                  <ErrorMessage>{errors.first_name}</ErrorMessage>
                )}
              </Flex>
              <Flex vertical gap="small" className={styles.widthOneThird}>
                <SInput placeholder="Soyisim" {...getFieldProps('last_name')} />
                {touched.last_name && errors.last_name && (
                  <ErrorMessage>{errors.last_name}</ErrorMessage>
                )}
              </Flex>
              <Flex vertical gap="small" className={styles.widthOneThird}>
                <SInput placeholder="TCKN" {...getFieldProps('turkish_id')} />
                {touched.turkish_id && errors.turkish_id && (
                  <ErrorMessage>{errors.turkish_id}</ErrorMessage>
                )}
              </Flex>
            </Flex>
            <Flex gap="small">
              <Flex vertical gap="small" className={styles.widthHalf}>
                <SDatePicker
                  id="birthday"
                  name="birthday"
                  value={
                    values.birthday ? dayjs(values.birthday, DATE_FORMAT) : null
                  }
                  placeholder="Doğum Tarihi"
                  onChange={(date) => {
                    if (date) {
                      setFieldValue('birthday', date.format(DATE_FORMAT));
                    }
                  }}
                />
                {touched.birthday && errors.birthday && (
                  <ErrorMessage>{errors.birthday}</ErrorMessage>
                )}
              </Flex>
              <Flex vertical gap="small" className={styles.widthHalf}>
                <SInput placeholder="GSM" {...getFieldProps('gsm')} />
                {touched.gsm && errors.gsm && (
                  <ErrorMessage>{errors.gsm}</ErrorMessage>
                )}
              </Flex>
            </Flex>
            <Flex gap="small">
              <Flex vertical gap="small" className={styles.widthHalf}>
                <SDropdown
                  id="gender"
                  value={values.gender}
                  placeholder="Cinsiyet"
                  options={[
                    { value: 'male', label: 'Erkek' },
                    { value: 'female', label: 'Kadın' },
                  ]}
                  onChange={(value) => setFieldValue('gender', value)}
                />
                {touched.gender && errors.gender && (
                  <ErrorMessage>{errors.gender}</ErrorMessage>
                )}
              </Flex>
              <Flex vertical gap="small" className={styles.widthHalf}>
                <SDropdown
                  id="nationality"
                  value={values.nationality}
                  placeholder="Uyruk"
                  options={[
                    { value: 'tc', label: 'TC' },
                    { value: 'other', label: 'Diğer' },
                  ]}
                  onChange={(value) => setFieldValue('nationality', value)}
                />
                {touched.nationality && errors.nationality && (
                  <ErrorMessage>{errors.nationality.toString()}</ErrorMessage>
                )}
              </Flex>
            </Flex>
            <Flex gap="small">
              <Flex vertical gap="small" className={styles.widthOneThird}>
                <SDropdown
                  id="payment"
                  value={values.payment}
                  placeholder="Ödeme"
                  options={props.dependentPayments.map((item) => ({
                    value: item.key,
                    label: item.value,
                  }))}
                  onChange={(value) => setFieldValue('payment', value)}
                />
                {touched.payment && errors.payment && (
                  <ErrorMessage>{errors.payment}</ErrorMessage>
                )}
              </Flex>
              <Flex vertical gap="small" className={styles.widthOneThird}>
                <SDropdown
                  id="dependent"
                  value={values.dependent}
                  placeholder="Bağlı Kişi"
                  options={props.peopleForOrg.map((item) => ({
                    value: item.key,
                    label: item.value,
                  }))}
                  onChange={(value) => setFieldValue('dependent', value)}
                />
                {touched.dependent && errors.dependent && (
                  <ErrorMessage>{errors.dependent}</ErrorMessage>
                )}
              </Flex>
              <Flex vertical gap="small" className={styles.widthOneThird}>
                <SDropdown
                  id="dependent_type"
                  value={values.dependent_type}
                  placeholder="Bağlımı Tipi"
                  options={Object.entries(DependentTypeEnum).map(
                    ([value, label]) => ({
                      value,
                      label,
                    }),
                  )}
                  onChange={(value) => setFieldValue('dependent_type', value)}
                />
                {touched.dependent_type && errors.dependent_type && (
                  <ErrorMessage>{errors.dependent_type}</ErrorMessage>
                )}
              </Flex>
            </Flex>
          </Flex>
          {props.type === modalTypes.EDIT && organization === 1 && (
            <Flex justify="space-between">
              <SLabelTitle>Durum Bilgisi</SLabelTitle>
              <StatusDropdown
                status={values.recent_status}
                statusChoices={props.statusChoices}
                onChange={(value) => setFieldValue('recent_status', value)}
              />
            </Flex>
          )}
          {props.type === modalTypes.EDIT && (
            <SMailSelector
              value={values.email_users}
              placeholder="Mail Listesi"
              options={props.withViewPerm?.map((wvp) => ({
                value: wvp.value,
                label: wvp.label,
              }))}
              onChange={(value) => setFieldValue('email_users', value)}
            />
          )}
        </Flex>
      </Flex>
    </ModalDialog>
  );
};

export default GroupHealthInsuranceDependentsModal;
